import { NavLink } from 'react-router-dom';
import { X } from 'lucide-react';
import { iconoDe, NOMBRE_DE_GRUPO } from './iconos-del-menu';
import { useUiStore } from '@/store/ui.store';
import { useMenu } from '@/hooks/useMenu';
import type { MenuResponse } from '@/api/modulos.api';
import { cn } from '@/lib/cn';
import { NEGOCIO } from '@/lib/negocio';
import { Sello } from './Sello';

/**
 * FASE 4 (El Edificio) — EL SIDEBAR COME DEL REGISTRO DE MÓDULOS.
 *
 * Antes tenía una lista de 19 items cableada aquí adentro, con banderas
 * `admin` y `owner` para esconder links. Eso hacía dos cosas mal:
 *
 * 1. **Dejaba la Fase 3 sin efecto.** El servidor decidía qué módulos tiene
 *    cada inquilino y este componente le mostraba los mismos 19 links a todos.
 *    Un cliente que no compró Trabajos veía «Trabajos» igual.
 * 2. **Escondía botones en vez de no tenerlos.** Un link oculto sigue estando
 *    en el bundle que descarga cualquiera; el prompt lo dice sin rodeos:
 *    esconder un botón no es seguridad.
 *
 * Ahora la lista viene de `GET /api/modulos/menu`. El servidor ya aplicó el rol
 * EFECTIVO dentro de ese negocio (que puede no ser el rol global del usuario) y
 * los módulos activos del inquilino. **Aquí no se filtra nada: solo se dibuja.**
 */

/**
 * La VISTA, sin hooks: recibe todo por props. Se separa asi para poder probarla
 * con `renderToStaticMarkup`, que es la convencion del repo (ver
 * `BotControlToggleView`). El contenedor de abajo es el que habla con el
 * servidor.
 */
export function SidebarView({
  sidebarOpen,
  datos,
  cargando,
  fallo,
  sinNegocio = false,
  className,
  alCerrar,
}: {
  sidebarOpen: boolean;
  datos: MenuResponse | undefined;
  cargando: boolean;
  fallo: boolean;
  /** Ya se sabe qué negocios hay y no hay ninguno activo. */
  sinNegocio?: boolean;
  /** Clases de más para el contenedor (el menú del teléfono lo pega al borde). */
  className?: string;
  /** Si viene, sale una «X» arriba a la derecha (el menú del teléfono). */
  alCerrar?: () => void;
}) {
  return (
    <aside
      className={cn(
        // 13-oct: el lateral flota dentro del marco, con las esquinas redondas (composición de Cielo).
        'flex shrink-0 flex-col overflow-hidden rounded-3xl border border-surface-border bg-white transition-all duration-200 ' +
          'dark:border-slate-800 dark:bg-slate-900',
        sidebarOpen ? 'w-64' : 'w-0 overflow-hidden lateral:w-16',
        className,
      )}
    >
      <div
        data-tour="logo"
        className="flex h-16 items-center gap-3 px-4"
      >
        {/* 13-oct, Kervens: arriba va el nombre del negocio, con su sello; el sistema no se nombra. */}
        {/* Con un negocio activo, su sello; sin él, el isotipo de Silva Notion (la casa). */}
        {datos?.negocio ? (
          <Sello nombre={datos.negocio} />
        ) : (
          <img src="/silva-notion-isotipo.png" alt={NEGOCIO} className="h-9 w-9 shrink-0 object-contain" />
        )}
        {sidebarOpen && (
          <span className="min-w-0 truncate text-base font-bold leading-tight text-slate-900 dark:text-slate-50">
            {datos?.negocio ?? NEGOCIO}
          </span>
        )}
        {alCerrar && (
          <button
            type="button"
            onClick={alCerrar}
            data-cerrar-menu
            aria-label="Cerrar el menú"
            className="-mr-2 ml-auto grid h-11 w-11 shrink-0 place-items-center rounded-full text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <nav className="flex-1 space-y-4 overflow-y-auto overscroll-contain p-3">
        {cargando && (
          // Sin esto, al cambiar de negocio el sidebar parpadea vacío y parece
          // que se rompió.
          <p className="px-3 py-2 text-xs text-slate-400">Cargando el menú…</p>
        )}

        {sinNegocio && (
          // ESTE es el estado que antes se veía como "Cargando el menú…" para
          // siempre. Que diga qué pasa importa: un spinner eterno hace pensar
          // que el sistema es lento, y esto no se arregla esperando.
          <p className="px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
            No hay un negocio activo. Elige uno arriba; si el selector está
            vacío, tu usuario todavía no tiene acceso a ninguno.
          </p>
        )}

        {fallo && (
          // Se dice qué pasó y qué hacer. Un sidebar vacío sin explicación se
          // lee como «el sistema se rompió».
          <p className="px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
            No se pudo cargar el menú. Recarga la página; si sigue igual, revisa
            que la API esté arriba.
          </p>
        )}

        {datos?.menu.map((grupo) => (
          <div key={grupo.grupo}>
            {sidebarOpen && (
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                {NOMBRE_DE_GRUPO[grupo.grupo] ?? grupo.grupo}
              </p>
            )}
            <div className="space-y-1">
              {grupo.items.map((item) => {
                const Icono = iconoDe(item.icono);
                return (
                  <NavLink
                    key={item.ruta}
                    to={item.ruta}
                    end={item.ruta === '/'}
                    data-tour={item.ruta}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                          : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
                      )
                    }
                  >
                    <Icono className="h-5 w-5 shrink-0" />
                    {sidebarOpen && <span className="truncate">{item.etiqueta}</span>}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  );
}

/** Contenedor: pide el menu del negocio activo y se lo pasa a la vista. */
export function Sidebar() {
  const sidebarOpen = useUiStore((s) => s.sidebarOpen);
  const { datos, cargando, fallo, sinNegocio } = useMenu();
  return (
    <SidebarView
      sidebarOpen={sidebarOpen}
      datos={datos}
      cargando={cargando}
      fallo={fallo}
      sinNegocio={sinNegocio}
    />
  );
}
